import React, { ReactNode, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import {
    Button,
    ButtonToolbar,
    Checkbox,
    CheckPicker,
    Input,
    InputGroup,
    IconButton,
    TagGroup,
    Tag,
    List,
    toaster,
    Message,
} from "rsuite";

import SearchIcon from "@rsuite/icons/Search";
import FunnelIcon from "@rsuite/icons/Funnel";
import ArrowUpLineIcon from "@rsuite/icons/ArrowUpLine";
import ArrowDownLineIcon from "@rsuite/icons/ArrowDownLine";
import SortAscIcon from "@rsuite/icons/SortAsc";
import SortByIcon from "@rsuite/icons/SortBy";
import SortDescIcon from "@rsuite/icons/SortDesc";

import { sendErpPackageEmails } from "api/deliverables/erp-packages-api";
import { loadCustomersDeliverablesInfo } from "api/customers/customers-api";
import { getErrorMessage } from "api/defaults";

import { ICustomerDeliverablesInfo } from "store/customers/customers-types";
import { IEmailTemplateBase } from "store/deliverables/email-templates/email-templates-types";
import { AppDispatch, RootState } from "store/store";
import { setModalLoaderVisibility } from "store/common/modal-window/modal-window-slices";
import { setErrorResponseMessageForModal } from "store/common/initialization/initialization-actions";
import {
    IErpPackageEmailsSending,
    IEmailRecipient,
    IErpPackage,
} from "store/deliverables/erp-packages/erp-packages-types";
import {
    EmailRecipientType,
    IEmailRecipient as ICustomerEmailRecipient,
} from "store/customers/customer-details/customer-details-types";

import constants from "utils/constants";

import ModalContainer from "components/Common/Modal/ModalContainer";

import "./EmailsSending.less";

enum SortBy {
    CustomerName,
    RecipientCount,
}

enum SortOrder {
    Asc = "asc",
    Desc = "desc",
}

interface ComponentProps {
    isOpened: boolean;
    erpPackage: IErpPackage | null;
    refreshPackages: () => void;
    setIsEmailsSendingOpened: (isOpened: boolean) => void;
}

const EmailsSending: React.FC<ComponentProps> = ({
    isOpened,
    erpPackage,
    refreshPackages,
    setIsEmailsSendingOpened,
}: ComponentProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const isModalBusy = useSelector(
        (state: RootState) => state.modalWindowReducer.isModalLoaderVisible
    );
    const [isDataFetched, setIsDataFetched] = useState<boolean>(false);
    const [selectedEmailTemplates, setSelectedEmailTemplates] = useState<
        IEmailTemplateBase[]
    >([]);
    const [emailTemplates, setEmailTemplates] = useState<Record<string, any>[]>(
        []
    );
    const [customers, setCustomers] = useState<ICustomerDeliverablesInfo[]>([]);
    const [allCustomers, setAllCustomers] = useState<
        ICustomerDeliverablesInfo[]
    >([]);
    const [checkedEnvs, setCheckedEnvs] = useState<string[]>([]);
    const [searchValue, setSearchValue] = useState<string>("");
    const [isAllChecked, setAllChecked] = useState<boolean>(false);
    const [checkedCustomers, setCheckedCustomers] = useState<IEmailRecipient[]>(
        []
    );
    const [emailSendingData, setEmailSendingData] =
        useState<IErpPackageEmailsSending | null>(null);
    const [expandedRowKey, setExpandedRowKey] = useState<string>("");

    const [sortBy, setSortBy] = useState<SortBy>(SortBy.CustomerName);
    const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.Asc);

    const sortByCustomerName = sortBy === SortBy.CustomerName;
    const sortByRecipientCount = sortBy === SortBy.RecipientCount;
    const isOrderAsc = sortOrder === SortOrder.Asc;

    const fetchData = () => {
        dispatch(setModalLoaderVisibility(true));
        loadCustomersDeliverablesInfo().then(([payload, status]) => {
            dispatch(setModalLoaderVisibility(false));
            setIsDataFetched(true);
            if (payload && status === constants.statusCode.OK) {
                setAllCustomers(payload);
                setEmailTemplates(
                    Array.from(
                        new Map(
                            payload
                                .map((customer) => customer.emailTemplate)
                                .filter((template) => template !== null)
                                .map((template) => [template?.id, template])
                        ).values()
                    ).map((template) => ({
                        value: {
                            id: template?.id,
                            name: template?.name,
                            locale: template?.locale,
                        },
                        label: template?.name,
                    }))
                );
            } else {
                const errorMessage = getErrorMessage(String(payload), status);
                dispatch(setErrorResponseMessageForModal(errorMessage));
            }
        });
    };

    const handleCheckboxCheck = (value: ICustomerDeliverablesInfo) => {
        if (isCustomerChecked(value.id)) {
            setCheckedCustomers(
                checkedCustomers.filter((customer) => customer.id !== value.id)
            );
        } else if (value.emailTemplate !== null) {
            setCheckedCustomers([
                ...checkedCustomers,
                {
                    id: value.id,
                    name: value.name,
                    templateId: value.emailTemplate.id,
                },
            ]);
        }
    };

    const handleModalClose = () => {
        setEmailTemplates([]);
        setAllChecked(false);
        setSearchValue("");
        setCheckedEnvs([]);
        setAllChecked(false);
        setCheckedCustomers([]);
        setEmailSendingData(null);
        setIsDataFetched(false);
        setIsEmailsSendingOpened(false);
        setSelectedEmailTemplates([]);
    };

    const handleSendPackage = () => {
        if (erpPackage && emailSendingData) {
            dispatch(setModalLoaderVisibility(true));
            sendErpPackageEmails(erpPackage?.id, emailSendingData).then(
                ([result, status]) => {
                    if (status === constants.statusCode.OK) {
                        handleModalClose();
                        refreshPackages();
                    } else {
                        toaster.push(
                            <Message type="error" showIcon>
                                {getErrorMessage(String(result), status)}
                            </Message>,
                            {
                                duration: constants.errorAlertDurationMsec,
                            }
                        );
                    }
                    dispatch(setModalLoaderVisibility(false));
                }
            );
        }
    };

    const handleRowExpand = (rowData: ICustomerDeliverablesInfo) => {
        let open = false;
        if (expandedRowKey === rowData.id) {
            open = true;
        }
        open ? setExpandedRowKey("") : setExpandedRowKey(rowData.id);
    };

    const getGroupedRecipients = (recipients: ICustomerEmailRecipient[]) => {
        const recipientGroups: any = {};
        recipients.forEach((recipient: ICustomerEmailRecipient) => {
            if (!recipientGroups[recipient.type]) {
                recipientGroups[recipient.type] = [];
            }
            recipientGroups[recipient.type].push(recipient);
        });

        return recipientGroups;
    };

    const isCustomerChecked = (customerId: string) => {
        return checkedCustomers.some((c) => c.id === customerId);
    };

    useEffect(() => {
        setCheckedCustomers(
            isAllChecked
                ? customers.map((customer) => ({
                      id: customer.id,
                      name: customer.name,
                      templateId: customer.emailTemplate!.id ?? null,
                  }))
                : []
        );
    }, [isAllChecked]);

    useEffect(() => {
        if (
            customers.length > 0 &&
            customers.length === checkedCustomers.length
        ) {
            setAllChecked(true);
        } else if (checkedCustomers.length === 0) {
            setAllChecked(false);
        }
    }, [checkedCustomers]);

    useEffect(() => {
        if (
            checkedCustomers.length &&
            selectedEmailTemplates &&
            erpPackage?.id
        ) {
            setEmailSendingData({
                ...emailSendingData,
                packageId: erpPackage.id,
                recipients: checkedCustomers,
            });
        }
    }, [checkedCustomers, selectedEmailTemplates, erpPackage]);

    useEffect(() => {
        if (!isDataFetched && isOpened) {
            fetchData();
        }
    }, [isDataFetched, isOpened]);

    useEffect(() => {
        const sortedCustomers = [...allCustomers]
            .filter(
                (c) =>
                    c.emailTemplate !== null &&
                    selectedEmailTemplates
                        .map((template) => template.id)
                        .includes(c.emailTemplate.id) &&
                    (!checkedEnvs.length ||
                        c.dooapEnvironments.some((env) =>
                            checkedEnvs.includes(env)
                        )) &&
                    c.name.toLowerCase().includes(searchValue.toLowerCase())
            )
            .sort((a, b) => {
                const orderMultiplier = isOrderAsc ? 1 : -1;
                if (sortBy === SortBy.CustomerName) {
                    return orderMultiplier * a.name.localeCompare(b.name);
                } else if (sortBy === SortBy.RecipientCount) {
                    return (
                        orderMultiplier *
                        (a.emailRecipients.length - b.emailRecipients.length)
                    );
                }

                return 0;
            });

        setCustomers(sortedCustomers);
    }, [
        selectedEmailTemplates,
        searchValue,
        checkedEnvs,
        allCustomers,
        sortBy,
        sortOrder,
    ]);

    const renderFilterMenuItem = (label: ReactNode) => {
        return <div style={{ marginTop: "0.125rem" }}>{label}</div>;
    };

    const header = <h1>{t("deliverables.packages.sendModal.title")}</h1>;
    const footer = (
        <ButtonToolbar
            className={cn(
                "custom-modal__button-toolbar",
                "emails-sending__button-toolbar"
            )}
        >
            <Button
                type="submit"
                appearance="primary"
                onClick={handleSendPackage}
                disabled={!checkedCustomers.length || isModalBusy}
            >
                {t("deliverables.packages.sendModal.confirmBtn")}
            </Button>
            <Button
                appearance="ghost"
                onClick={handleModalClose}
                disabled={isModalBusy}
            >
                {t("deliverables.packages.sendModal.cancelBtn")}
            </Button>
        </ButtonToolbar>
    );

    return (
        <ModalContainer
            className="custom-modal__container"
            backdrop="static"
            open={isOpened}
            onClose={handleModalClose}
            header={header}
            footer={footer}
        >
            <div>
                <div className="emails-sending__details-section">
                    <div className="emails-sending__details-section-type">
                        {erpPackage?.packageType.type}
                    </div>
                    <div>{erpPackage?.description}</div>
                </div>
                <div className="emails-sending__target-section">
                    {t("deliverables.packages.sendModal.targetTitle") +
                        ": " +
                        erpPackage?.packageTarget.target}
                </div>
                <div className="emails-sending__form-section">
                    <p className="emails-sending__field-label">
                        {t("deliverables.packages.sendModal.templateTitle")}
                    </p>
                    <CheckPicker
                        placeholder={t(
                            "deliverables.packages.sendModal.templatePlaceholder"
                        )}
                        placement={"bottom"}
                        searchable={false}
                        data={emailTemplates}
                        value={selectedEmailTemplates}
                        onChange={(values: IEmailTemplateBase[]) => {
                            setSelectedEmailTemplates(values);
                            setExpandedRowKey("");
                        }}
                        renderMenuItem={renderFilterMenuItem}
                        className="emails-sending__select-picker"
                    />
                    <div className="emails-sending__filter-section">
                        <InputGroup className="emails-sending__search-section">
                            <InputGroup.Addon>
                                <SearchIcon />
                            </InputGroup.Addon>
                            <Input
                                value={searchValue}
                                onChange={setSearchValue}
                            />
                        </InputGroup>
                        <FunnelIcon className="customers__filter-icon" />
                        <CheckPicker
                            placement={"bottom"}
                            searchable={false}
                            data={Array.from(
                                new Set(
                                    allCustomers
                                        .map(
                                            (customer) =>
                                                customer.dooapEnvironments
                                        )
                                        .flat()
                                )
                            ).map((env) => ({
                                value: env,
                                label: env,
                            }))}
                            value={checkedEnvs}
                            onChange={setCheckedEnvs}
                            renderMenuItem={renderFilterMenuItem}
                            className="emails-sending__check-picker"
                        />
                    </div>
                    <div>
                        <div className="emails-sending__customers-header">
                            <Checkbox
                                checked={isAllChecked}
                                onChange={() => {
                                    setAllChecked(!isAllChecked);
                                }}
                                disabled={!emailTemplates || !customers.length}
                            />
                            <div
                                onClick={() => {
                                    setSortBy(SortBy.CustomerName);
                                    setSortOrder(
                                        isOrderAsc
                                            ? SortOrder.Desc
                                            : SortOrder.Asc
                                    );
                                }}
                            >
                                {t(
                                    "deliverables.packages.sendModal.customersTitle"
                                )}
                                {sortByCustomerName ? (
                                    isOrderAsc ? (
                                        <SortAscIcon />
                                    ) : (
                                        <SortDescIcon />
                                    )
                                ) : (
                                    <SortByIcon />
                                )}
                            </div>
                            <div
                                onClick={() => {
                                    setSortBy(SortBy.RecipientCount);
                                    setSortOrder(
                                        isOrderAsc
                                            ? SortOrder.Desc
                                            : SortOrder.Asc
                                    );
                                }}
                            >
                                {t(
                                    "deliverables.packages.sendModal.recipientCountTitle"
                                )}
                                {sortByRecipientCount ? (
                                    isOrderAsc ? (
                                        <SortAscIcon />
                                    ) : (
                                        <SortDescIcon />
                                    )
                                ) : (
                                    <SortByIcon />
                                )}
                            </div>
                        </div>
                        {selectedEmailTemplates.length !== 0 ? (
                            <List
                                size="sm"
                                className="emails-sending__customers-list"
                            >
                                {customers.map((customer, index) => {
                                    return (
                                        <>
                                            <List.Item
                                                key={index}
                                                index={index}
                                            >
                                                <Checkbox
                                                    checked={isCustomerChecked(
                                                        customer.id
                                                    )}
                                                    onChange={() => {
                                                        handleCheckboxCheck(
                                                            customer
                                                        );
                                                    }}
                                                >
                                                    {customer.name}
                                                </Checkbox>
                                                <div
                                                    className={
                                                        customer.emailRecipients
                                                            .length
                                                            ? "emails-sending__customers-list-text--normal"
                                                            : isCustomerChecked(
                                                                    customer.id
                                                                )
                                                              ? "emails-sending__customers-list-text--error"
                                                              : "emails-sending__customers-list-text--warning"
                                                    }
                                                >
                                                    <span>
                                                        {customer
                                                            .emailRecipients
                                                            .length +
                                                            t(
                                                                "deliverables.packages.sendModal.recipientsTitle"
                                                            )}
                                                    </span>
                                                    <IconButton
                                                        size="xs"
                                                        appearance="subtle"
                                                        onClick={() => {
                                                            handleRowExpand(
                                                                customer
                                                            );
                                                        }}
                                                        disabled={
                                                            !customer
                                                                .emailRecipients
                                                                .length
                                                        }
                                                        icon={
                                                            expandedRowKey ===
                                                            customer.id ? (
                                                                <ArrowUpLineIcon />
                                                            ) : (
                                                                <ArrowDownLineIcon />
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </List.Item>
                                            {expandedRowKey === customer.id && (
                                                <div className="emails-sending__customers-list--expanded">
                                                    {Object.keys(
                                                        getGroupedRecipients(
                                                            customer.emailRecipients
                                                        )
                                                    ).map((type) => (
                                                        <div
                                                            key={type}
                                                            className="emails-sending__customers-recipients"
                                                        >
                                                            <span>
                                                                {Number(
                                                                    type
                                                                ) ===
                                                                EmailRecipientType.Main
                                                                    ? t(
                                                                          "customerDetails.packageDelivery.mainRecipientTitle"
                                                                      )
                                                                    : t(
                                                                          "customerDetails.packageDelivery.copyRecipientTitle"
                                                                      )}
                                                            </span>
                                                            <TagGroup>
                                                                {getGroupedRecipients(
                                                                    customer.emailRecipients
                                                                )[type].map(
                                                                    (
                                                                        recipient: ICustomerEmailRecipient,
                                                                        index: number
                                                                    ) => (
                                                                        <Tag
                                                                            key={
                                                                                index
                                                                            }
                                                                        >
                                                                            {
                                                                                recipient.address
                                                                            }
                                                                        </Tag>
                                                                    )
                                                                )}
                                                            </TagGroup>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    );
                                })}
                            </List>
                        ) : (
                            <div className="emails-sending__customers-empty">
                                {t(
                                    "deliverables.packages.sendModal.emptyTableMessage"
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </ModalContainer>
    );
};

export default EmailsSending;
